import { htmlService } from "../../../core/services/html.service";
import { dateParserService } from "../../../core/services/date-parser.service";
import { DOT, STYLES, MESSAGE_WIDTH_MODE, REFERENCES } from "../../constants";
import { renderMessage, renderAttachments } from "../../templates";
import type { IChatOptions, IMessage, IMessageReferenceData, IAuthor, IChatMessages, IMessageSettings, IToolbarAction, MessageStatusTemplateFunction } from "../../models";
import type { DataManager } from "../data-manager.collaborator";
import type { IKendoJQuery } from "../../../core";

declare const $: any;

export interface IMessageRendererContext {
    list: IKendoJQuery;
    options: IChatOptions;
    dataManager: DataManager;
    dataItem: (message: IKendoJQuery) => IMessage | null;
    getReplyMessage: (message: IMessage) => IMessageReferenceData | null;
    initMessageToolbar: (element: IKendoJQuery, actions?: IToolbarAction[]) => void;
    initFileMenus: (element: IKendoJQuery, enableFileActions?: boolean) => void;
}

export class MessageRenderer {
    private context: IMessageRendererContext;

    constructor(context: IMessageRendererContext) {
        this.context = context;
    }

    renderMessage(message: IMessage): void {
        const options = this.context.options;
        const componentMessages = options.messages;
        const author: IAuthor = {
            id: message.authorId,
            name: message.authorName,
            imageUrl: message.authorImageUrl,
            imageAltText: message.authorImageAltText
        };
        const isOwnMessage = String(author.id) === String(options.authorId);
        const replyMessage = this.context.getReplyMessage(message);
        const messageTimeFormat = options.messageTimeFormat;
        const skipSanitization = options.skipSanitization;
        const statusTemplate = options.messageStatusTemplate;
        const userSettings = isOwnMessage ? options.authorMessageSettings : options.receiverMessageSettings;
        const messageSettings = this._mergeMessageSettings(options, userSettings);
        const expandable = messageSettings.allowMessageCollapse ?? options.allowMessageCollapse;
        const fullWidth = messageSettings.messageWidthMode === MESSAGE_WIDTH_MODE.FULL;

        if (this._handleExistingMessageUpdate({
            message, author, isOwnMessage, replyMessage, componentMessages, expandable, messageTimeFormat, skipSanitization, statusTemplate, messageSettings
        })) {
            return;
        }

        const targetGroupElement = this._findTargetMessageGroup();
        const canGroup = this._canGroupWithLastMessage(targetGroupElement, author.id);

        if (canGroup && targetGroupElement.length) {
            this._addMessageToExistingGroup({
                message, author, isOwnMessage, replyMessage, componentMessages, expandable, targetGroupElement, messageTimeFormat, skipSanitization, statusTemplate, messageSettings
            });
        } else {
            const showTimestamp = this._shouldShowTimestamp(message);
            this._createNewMessageGroup({
                message, author, isOwnMessage, replyMessage, componentMessages, expandable, fullWidth, messageTimeFormat, showTimestamp, skipSanitization, statusTemplate, messageSettings
            });
        }
    }

    _mergeMessageSettings(options: IChatOptions, userSettings: IMessageSettings | null | undefined): IMessageSettings {
        const merged: IMessageSettings = {
            showAvatar: options.showAvatar,
            showUsername: options.showUsername,
            allowMessageCollapse: options.allowMessageCollapse,
            messageWidthMode: options.messageWidthMode,
            messageToolbarActions: options.messageToolbarActions,
            messageActions: options.messageActions,
            messageTemplate: options.messageTemplate,
            messageContentTemplate: options.messageContentTemplate
        };

        if (userSettings) {
            if (userSettings.showAvatar !== undefined) merged.showAvatar = userSettings.showAvatar;
            if (userSettings.showUsername !== undefined) merged.showUsername = userSettings.showUsername;
            if (userSettings.allowMessageCollapse !== undefined) merged.allowMessageCollapse = userSettings.allowMessageCollapse;
            if (userSettings.messageWidthMode !== undefined) merged.messageWidthMode = userSettings.messageWidthMode;
            if (userSettings.enableFileActions !== undefined) merged.enableFileActions = userSettings.enableFileActions;
            if (userSettings.enableContextMenuActions !== undefined) merged.enableContextMenuActions = userSettings.enableContextMenuActions;
            if (userSettings.messageToolbarActions !== undefined) merged.messageToolbarActions = userSettings.messageToolbarActions;
            if (userSettings.messageActions !== undefined) merged.messageActions = userSettings.messageActions;
            if (userSettings.messageTemplate !== undefined) merged.messageTemplate = userSettings.messageTemplate;
            if (userSettings.messageContentTemplate !== undefined) merged.messageContentTemplate = userSettings.messageContentTemplate;
        }

        return merged;
    }

    _getMessageTimeVisibility(): IChatOptions["timestampVisibility"] {
        return this.context.options.timestampVisibility;
    }

    _shouldShowTimestamp(message: IMessage): boolean {
        if (!message.timestamp) {
            return false;
        }

        const lastNonTypingMessage = this.context.list.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length === 0;
        }).last() as IKendoJQuery;

        if (!lastNonTypingMessage.length) {
            return true;
        }

        const lastMessageData = this.context.dataItem(lastNonTypingMessage);
        if (!lastMessageData || !lastMessageData.timestamp) {
            return true;
        }

        const currentMessageDate = dateParserService.parseDate(message.timestamp);
        const lastMessageDate = dateParserService.parseDate(lastMessageData.timestamp);

        if (!currentMessageDate || !lastMessageDate) {
            return false;
        }

        const currentDay = new Date(currentMessageDate);
        currentDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(lastMessageDate);
        lastDay.setHours(0, 0, 0, 0);

        return currentDay.getTime() !== lastDay.getTime();
    }

    _shouldRenderUserStatus(author: IAuthor, messageSettings?: IMessageSettings): boolean {
        return messageSettings?.showAvatar !== undefined
            ? !!(messageSettings.showAvatar && author?.imageUrl)
            : !!author?.imageUrl;
    }

    _syncUserStatus(targetGroupElement: IKendoJQuery, message: IMessage, author: IAuthor, messageSettings?: IMessageSettings): void {
        const userStatusWrapper = targetGroupElement.find(`[${REFERENCES.userStatusWrapper}]`) as IKendoJQuery;
        const userStatusTemplate = this.context.options.userStatusTemplate;

        if (!userStatusTemplate || !this._shouldRenderUserStatus(author, messageSettings)) {
            userStatusWrapper.remove();
            return;
        }

        const userStatusElement = $(`<div ${REFERENCES.userStatusWrapper}>${userStatusTemplate({ message: { ...message, author } })}</div>`);

        userStatusWrapper.remove();
        targetGroupElement.prepend(userStatusElement);
    }

    _isLastMessageInGroup(messageElement: IKendoJQuery): boolean {
        const messageGroup = messageElement.closest(DOT + STYLES.messageGroup);
        const lastMessageInGroup = messageGroup.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length === 0;
        }).last() as IKendoJQuery;

        return lastMessageInGroup.is(messageElement);
    }

    _handleExistingMessageUpdate(context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage: IMessageReferenceData | null;
        componentMessages: IChatMessages;
        expandable: boolean;
        messageTimeFormat: string;
        skipSanitization: boolean;
        statusTemplate?: MessageStatusTemplateFunction | null;
        messageSettings?: IMessageSettings;
    }): boolean {
        const { message, author, isOwnMessage, replyMessage, componentMessages, expandable, messageTimeFormat, skipSanitization, statusTemplate, messageSettings } = context;
        const options = this.context.options;
        const existingMessageElement = this.context.list.find(DOT + STYLES.message + `[data-uid="${htmlService.encode(message.uid ?? "")}"]`);
        const messageTimeVisibility = this._getMessageTimeVisibility();
        const messageTemplate = messageSettings?.messageTemplate || null;
        const contentTemplate = messageSettings?.messageContentTemplate || null;
        const filesTemplate = options.filesTemplate;

        if (existingMessageElement.length) {
            const isLastMessageInGroup = this._isLastMessageInGroup(existingMessageElement as IKendoJQuery);

            let messageHtml: string;
            if (messageTemplate) {
                messageHtml = messageTemplate(
                    { ...message, isOwnMessage, author },
                    replyMessage,
                    true,
                    componentMessages,
                    messageSettings?.allowMessageCollapse ?? expandable,
                    messageTimeFormat,
                    skipSanitization,
                    statusTemplate
                );
            } else {
                messageHtml = renderMessage(
                    { ...message, isOwnMessage, author },
                    replyMessage,
                    true,
                    componentMessages,
                    messageSettings?.allowMessageCollapse ?? expandable,
                    messageTimeFormat,
                    skipSanitization,
                    statusTemplate,
                    options.filesLayoutMode,
                    filesTemplate,
                    contentTemplate,
                    options.attachmentTemplate,
                    options.attachmentLayout,
                    messageTimeVisibility,
                    options.messageStatusSettings,
                    messageTimeVisibility !== "hidden"
                );
            }

            const updatedMessageElement = $(messageHtml);
            updatedMessageElement.data("messageData", message);
            existingMessageElement.replaceWith(updatedMessageElement);
            this.context.initMessageToolbar(updatedMessageElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery, messageSettings?.messageToolbarActions);
            this.context.initFileMenus(updatedMessageElement, messageSettings?.enableFileActions);

            if (isLastMessageInGroup) {
                this._syncUserStatus(updatedMessageElement.closest(DOT + STYLES.messageGroup) as IKendoJQuery, message, author, messageSettings);
            }

            return true;
        }
        return false;
    }

    _findTargetMessageGroup(): IKendoJQuery {
        let lastGroupElement = this.context.list.children(DOT + STYLES.messageGroup).last();
        let targetGroupElement = lastGroupElement;

        const messagesInLastGroup = lastGroupElement.find(DOT + STYLES.message);
        if (messagesInLastGroup.length === 1) {
            const onlyMessage = messagesInLastGroup.first();
            const isTypingIndicator = onlyMessage.find(DOT + STYLES.typingIndicator).length > 0;

            if (isTypingIndicator) {
                targetGroupElement = lastGroupElement.prev(DOT + STYLES.messageGroup);
            }
        }

        return targetGroupElement as IKendoJQuery;
    }

    _canGroupWithLastMessage(targetGroupElement: IKendoJQuery, authorId: string): boolean {
        const lastMessageInTargetGroup = targetGroupElement.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length === 0;
        }).last() as IKendoJQuery;

        const lastMessageData = this.context.dataItem(lastMessageInTargetGroup);
        return String(lastMessageData?.authorId) === String(authorId);
    }

    _addMessageToExistingGroup(context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage: IMessageReferenceData | null;
        componentMessages: IChatMessages;
        expandable: boolean;
        targetGroupElement: IKendoJQuery;
        messageTimeFormat: string;
        skipSanitization: boolean;
        statusTemplate?: MessageStatusTemplateFunction | null;
        messageSettings?: IMessageSettings;
    }): void {
        const { message, author, isOwnMessage, replyMessage, componentMessages, expandable, targetGroupElement, messageTimeFormat, skipSanitization, statusTemplate, messageSettings } = context;
        const options = this.context.options;
        const messageGroupContent = targetGroupElement.find(DOT + STYLES.messageGroupContent);
        const messageTimeVisibility = this._getMessageTimeVisibility();
        const messageTemplate = messageSettings?.messageTemplate || null;
        const contentTemplate = messageSettings?.messageContentTemplate || null;
        const filesTemplate = options.filesTemplate;

        let messageHtml: string;
        if (messageTemplate) {
            messageHtml = messageTemplate(
                { ...message, isOwnMessage, author },
                replyMessage,
                true,
                componentMessages,
                messageSettings?.allowMessageCollapse ?? expandable,
                messageTimeFormat,
                skipSanitization,
                statusTemplate
            );
        } else {
            messageHtml = renderMessage(
                { ...message, isOwnMessage, author },
                replyMessage,
                true,
                componentMessages,
                messageSettings?.allowMessageCollapse ?? expandable,
                messageTimeFormat,
                skipSanitization,
                statusTemplate,
                options.filesLayoutMode,
                filesTemplate,
                contentTemplate,
                options.attachmentTemplate,
                options.attachmentLayout,
                messageTimeVisibility,
                options.messageStatusSettings,
                messageTimeVisibility !== "hidden"
            );
        }

        const messageElement = $(messageHtml);
        messageElement.data("messageData", message);
        messageGroupContent.append(messageElement);
        this.context.initMessageToolbar(messageElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery, messageSettings?.messageToolbarActions);
        this.context.initFileMenus(messageElement, messageSettings?.enableFileActions);
        if (!message.isDeleted) {
            const effectiveAttachmentLayout = message.attachmentLayout || options.attachmentLayout || "list";
            const attachmentsHtml = renderAttachments(message.attachments, message, effectiveAttachmentLayout, options.attachmentTemplate);
            if (attachmentsHtml) {
                targetGroupElement.after($(attachmentsHtml));
            }
        }
        this._syncUserStatus(targetGroupElement, message, author, messageSettings);
        this._moveTypingIndicatorsToEnd();
    }

    _createNewMessageGroup(context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage: IMessageReferenceData | null;
        componentMessages: IChatMessages;
        expandable: boolean;
        fullWidth: boolean;
        messageTimeFormat: string;
        showTimestamp: boolean;
        skipSanitization: boolean;
        statusTemplate?: MessageStatusTemplateFunction | null;
        messageSettings?: IMessageSettings;
    }): IKendoJQuery {
        const { message, author, isOwnMessage, replyMessage, componentMessages, expandable, fullWidth, messageTimeFormat, showTimestamp, skipSanitization, statusTemplate, messageSettings } = context;
        const options = this.context.options;
        const messageGroupTemplate = options.messageGroupTemplate;
        const timestampTemplate = options.timestampTemplate;
        const messageTemplate = messageSettings?.messageTemplate || null;
        const contentTemplate = messageSettings?.messageContentTemplate || null;

        const groupElement = $(messageGroupTemplate({
            message,
            author,
            isOwnMessage,
            replyMessage,
            downloadAll: true,
            messages: componentMessages,
            expandable,
            fullWidth,
            messageTimeFormat,
            timestampTemplate,
            statusTemplate,
            showTimestamp,
            messageTemplate,
            contentTemplate,
            filesTemplate: options.filesTemplate,
            skipSanitization,
            messageSettings,
            filesLayoutMode: options.filesLayoutMode,
            attachmentTemplate: options.attachmentTemplate,
            attachmentLayout: options.attachmentLayout,
            userStatusTemplate: options.userStatusTemplate,
            timestampVisibility: this._getMessageTimeVisibility(),
            statusSettings: options.messageStatusSettings,
            showMessageTime: this._getMessageTimeVisibility() !== "hidden"
        }));

        groupElement.find(DOT + STYLES.message).first().data("messageData", message);

        this.context.list.append(groupElement);
        this.context.initMessageToolbar(groupElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery, messageSettings?.messageToolbarActions);
        this.context.initFileMenus(groupElement, messageSettings?.enableFileActions);
        this._moveTypingIndicatorsToEnd();

        return groupElement;
    }

    _moveTypingIndicatorsToEnd(): void {
        const renderer = this;
        const ctx = this.context;
        const options = ctx.options;
        const messageGroupTemplate = options.messageGroupTemplate;

        const typingMessages = ctx.list.find(DOT + STYLES.message).filter(function() {
            return $(this).find(DOT + STYLES.typingIndicator).length > 0;
        });

        typingMessages.each(function() {
            const typingMessage = $(this);
            const messageGroup = typingMessage.closest(DOT + STYLES.messageGroup);
            const messagesInGroup = messageGroup.find(DOT + STYLES.message);

            if (messagesInGroup.length === 1) {
                messageGroup.detach().appendTo(ctx.list);
            } else {
                typingMessage.remove();

                const message = ctx.dataItem(typingMessage);
                if (!message) return;

                const author: IAuthor = {
                    id: message.authorId,
                    name: message.authorName,
                    imageUrl: message.authorImageUrl,
                    imageAltText: message.authorImageAltText
                };
                const isOwnMessage = String(author.id) === String(options.authorId);
                const userSettings = isOwnMessage ? options.authorMessageSettings : options.receiverMessageSettings;
                const messageSettings = renderer._mergeMessageSettings(options, userSettings);

                const typingGroupElement = $(messageGroupTemplate({
                    message,
                    author,
                    isOwnMessage,
                    downloadAll: true,
                    messages: options.messages,
                    expandable: messageSettings.allowMessageCollapse ?? options.allowMessageCollapse,
                    fullWidth: messageSettings.messageWidthMode === MESSAGE_WIDTH_MODE.FULL,
                    messageTimeFormat: options.messageTimeFormat,
                    statusTemplate: options.messageStatusTemplate,
                    showTimestamp: false,
                    timestampTemplate: options.timestampTemplate,
                    messageTemplate: messageSettings.messageTemplate || null,
                    contentTemplate: messageSettings.messageContentTemplate || null,
                    filesTemplate: options.filesTemplate,
                    skipSanitization: options.skipSanitization,
                    messageSettings,
                    filesLayoutMode: options.filesLayoutMode,
                    attachmentTemplate: options.attachmentTemplate,
                    attachmentLayout: options.attachmentLayout,
                    userStatusTemplate: options.userStatusTemplate,
                    timestampVisibility: renderer._getMessageTimeVisibility(),
                    statusSettings: options.messageStatusSettings,
                    showMessageTime: renderer._getMessageTimeVisibility() !== "hidden"
                }));

                ctx.list.append(typingGroupElement);
            }
        });
    }

    prependMessages(messages: IMessage[]): void {
        if (!messages.length) {
            return;
        }

        const options = this.context.options;
        const componentMessages = options.messages;
        const messageGroupTemplate = options.messageGroupTemplate;
        const timestampTemplate = options.timestampTemplate;

        let currentGroupElement: any = null;
        let currentAuthorId: string | null = null;
        const fragment = document.createDocumentFragment();
        const $fragment = $(fragment);

        for (let i = 0; i < messages.length; i++) {
            const message = messages[i];
            const author: IAuthor = {
                id: message.authorId,
                name: message.authorName,
                imageUrl: message.authorImageUrl,
                imageAltText: message.authorImageAltText
            };
            const isOwnMessage = String(author.id) === String(options.authorId);
            const replyMessage = this.context.getReplyMessage(message);
            const userSettings = isOwnMessage ? options.authorMessageSettings : options.receiverMessageSettings;
            const messageSettings = this._mergeMessageSettings(options, userSettings);
            const statusTemplate = options.messageStatusTemplate;

            if (String(author.id) === currentAuthorId && currentGroupElement) {
                const messageGroupContent = currentGroupElement.find(DOT + STYLES.messageGroupContent);
                const messageTemplate = messageSettings.messageTemplate || null;
                const contentTemplate = messageSettings.messageContentTemplate || null;
                let messageHtml: string;
                if (messageTemplate) {
                    messageHtml = messageTemplate(
                        { ...message, isOwnMessage, author },
                        replyMessage,
                        true,
                        componentMessages,
                        messageSettings.allowMessageCollapse ?? options.allowMessageCollapse,
                        options.messageTimeFormat,
                        options.skipSanitization,
                        statusTemplate
                    );
                } else {
                    messageHtml = renderMessage(
                        { ...message, isOwnMessage, author },
                        replyMessage,
                        true,
                        componentMessages,
                        messageSettings.allowMessageCollapse ?? options.allowMessageCollapse,
                        options.messageTimeFormat,
                        options.skipSanitization,
                        statusTemplate,
                        options.filesLayoutMode,
                        options.filesTemplate,
                        contentTemplate,
                        options.attachmentTemplate,
                        options.attachmentLayout,
                        options.timestampVisibility,
                        options.messageStatusSettings,
                        options.timestampVisibility !== "hidden"
                    );
                }
                const messageElement = $(messageHtml);
                messageGroupContent.append(messageElement);
                this.context.initMessageToolbar(messageElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery, messageSettings?.messageToolbarActions);
                this.context.initFileMenus(messageElement, messageSettings?.enableFileActions);
            } else {
                const showTimestamp = i === 0 && message.timestamp;
                const messageTemplate = messageSettings.messageTemplate || null;
                const contentTemplate = messageSettings.messageContentTemplate || null;
                currentGroupElement = $(messageGroupTemplate({
                    message,
                    author,
                    isOwnMessage,
                    replyMessage,
                    downloadAll: true,
                    messages: componentMessages,
                    expandable: messageSettings.allowMessageCollapse ?? options.allowMessageCollapse,
                    fullWidth: messageSettings.messageWidthMode === MESSAGE_WIDTH_MODE.FULL,
                    messageTimeFormat: options.messageTimeFormat,
                    timestampTemplate,
                    statusTemplate,
                    showTimestamp: !!showTimestamp,
                    messageTemplate,
                    skipSanitization: options.skipSanitization,
                    messageSettings,
                    filesLayoutMode: options.filesLayoutMode,
                    contentTemplate,
                    attachmentTemplate: options.attachmentTemplate,
                    attachmentLayout: options.attachmentLayout,
                    userStatusTemplate: options.userStatusTemplate,
                    timestampVisibility: options.timestampVisibility,
                    statusSettings: options.messageStatusSettings,
                    showMessageTime: options.timestampVisibility !== "hidden"
                }));

                $fragment.append(currentGroupElement);
                this.context.initMessageToolbar(currentGroupElement.find(DOT + STYLES.messageToolbar) as IKendoJQuery, messageSettings?.messageToolbarActions);
                this.context.initFileMenus(currentGroupElement, messageSettings?.enableFileActions);
                currentAuthorId = String(author.id);
            }
        }

        const firstExistingGroup = this.context.list.children(DOT + STYLES.messageGroup).first();
        if (currentGroupElement && firstExistingGroup.length) {
            const lastPrependedAuthorId = currentAuthorId;
            const firstExistingMessage = firstExistingGroup.find(DOT + STYLES.message).first() as IKendoJQuery;
            const firstExistingData = this.context.dataItem(firstExistingMessage);

            if (firstExistingData && String(firstExistingData.authorId) === lastPrependedAuthorId) {
                const existingContent = firstExistingGroup.find(DOT + STYLES.messageGroupContent);
                const prependedContent = currentGroupElement.find(DOT + STYLES.messageGroupContent);
                const messagesToMove = prependedContent.children();
                existingContent.prepend(messagesToMove);
                currentGroupElement.remove();
            }
        }

        this.context.list.prepend(fragment);
    }
}
